import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { expenseKeys } from "@/lib/query";
import { ApiError, getJson, sendJson } from "@/lib/time/api-client";
import type {
  ExpenseApprovalsView,
  ExpenseDetail,
  ExpenseIdInput,
  ExpenseOptions,
  ExpensePendingView,
  ExpenseRejectInput,
  ExpenseSaveInput,
  ExpenseSaveResult,
  ExpenseScanResult,
  ExpenseStatusResult,
  ExpensesView,
  ReceiptInput,
  ReceiptResult,
  ReceiptScanInput,
} from "@/schemas/expenses";

export const useExpenses = (year: number) =>
  useQuery({
    queryKey: expenseKeys.year(year),
    queryFn: () => getJson<ExpensesView>(`/api/expenses?year=${year}`),
  });

export const useExpenseOptions = (enabled: boolean) =>
  useQuery({
    queryKey: expenseKeys.options(),
    queryFn: () => getJson<ExpenseOptions>("/api/expenses/options"),
    enabled,
    staleTime: 60_000,
  });

export const useExpenseClaim = (id: string | null) =>
  useQuery({
    queryKey: expenseKeys.claim(id ?? ""),
    queryFn: () => getJson<ExpenseDetail>(`/api/expenses/claim?id=${id}`),
    enabled: id !== null,
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
  });

export const useExpenseApprovals = (enabled: boolean) =>
  useQuery({
    queryKey: expenseKeys.approvals(),
    queryFn: () => getJson<ExpenseApprovalsView>("/api/expenses/approvals"),
    enabled,
  });

/** The nav badge, Home banner and To approve tab; refreshed every 5 minutes for the badge. */
export const useExpensePending = (enabled = true) =>
  useQuery({
    queryKey: expenseKeys.pending(),
    queryFn: () => getJson<ExpensePendingView>("/api/expenses/pending"),
    enabled,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

const errorText = (err: unknown) =>
  err instanceof ApiError ? err.message : "Something went wrong. Try again.";

function useExpenseWrite<I, R>(url: string, done: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: I) => sendJson<R>("POST", url, input),
    onSuccess: () => {
      toast.success(done);
      void qc.invalidateQueries({ queryKey: expenseKeys.all });
    },
    onError: (err) => toast.error(errorText(err)),
  });
}

/** Reads receipts for the form. Saves nothing; a failure is a toast and the form stays as it is. */
export function useScanReceipt() {
  return useMutation({
    mutationFn: (input: ReceiptScanInput) =>
      sendJson<ExpenseScanResult>("POST", "/api/expenses/scan", input),
    onError: (err) => toast.error(errorText(err)),
  });
}

export const useDeleteClaim = () =>
  useExpenseWrite<ExpenseIdInput, { id: string }>("/api/expenses/claim/delete", "Deleted");
export const useCopyClaim = () =>
  useExpenseWrite<ExpenseIdInput, { id: string }>("/api/expenses/claim/copy", "Copied to a draft");
export const useSubmitClaim = () =>
  useExpenseWrite<ExpenseIdInput, ExpenseStatusResult>("/api/expenses/claim/submit", "Submitted");
export const useApproveClaim = () =>
  useExpenseWrite<ExpenseIdInput, ExpenseStatusResult>(
    "/api/expenses/approvals/approve",
    "Approved",
  );
export const useRejectClaim = () =>
  useExpenseWrite<ExpenseRejectInput, ExpenseStatusResult>(
    "/api/expenses/approvals/reject",
    "Rejected",
  );

export interface SaveClaimRequest {
  claim: ExpenseSaveInput;
  /** Files to add, by the index of their line in `claim.lines`. */
  uploads: Array<{ lineIndex: number; fileName: string; data: string }>;
  removeReceiptIds: string[];
  submit: boolean;
}

export type SaveClaimOutcome =
  | { kind: "done"; id: string }
  /** The claim is saved, not submitted; a later step failed. Finish it from its detail. */
  | { kind: "partial"; id: string; message: string };

/**
 * Header and lines, then receipts one by one, then submit. Receipts can't share the save call
 * (each is up to 6 MB and is stored by its own gateway call), so a failure after the save keeps
 * the claim unsubmitted (draft or rejected, as it was) and opens it, rather than asking to fill the form again.
 */
export function useSaveClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: SaveClaimRequest): Promise<SaveClaimOutcome> => {
      const saved = await sendJson<ExpenseSaveResult>("POST", "/api/expenses/claim", req.claim);
      try {
        for (const id of req.removeReceiptIds) {
          await sendJson<ReceiptResult>("POST", "/api/expenses/receipt/delete", { id });
        }
        for (const u of req.uploads) {
          const lineId = saved.lineIds[u.lineIndex];
          if (!lineId) continue;
          const input: ReceiptInput = { lineId, fileName: u.fileName, data: u.data };
          await sendJson<ReceiptResult>("POST", "/api/expenses/receipt", input);
        }
        if (req.submit) {
          const input: ExpenseIdInput = { id: saved.id };
          await sendJson<ExpenseStatusResult>("POST", "/api/expenses/claim/submit", input);
        }
      } catch (err) {
        return { kind: "partial", id: saved.id, message: errorText(err) };
      }
      return { kind: "done", id: saved.id };
    },
    onSuccess: (out, req) => {
      if (out.kind === "done") toast.success(req.submit ? "Submitted" : "Saved");
      else toast.error(`Saved, not finished. ${out.message}`);
      void qc.invalidateQueries({ queryKey: expenseKeys.all });
    },
    onError: (err) => toast.error(errorText(err)),
  });
}

/** Base64 of a picked file, without the data: prefix. */
export async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let text = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    text += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(text);
}
